import { vec2, vec3 } from "gl-matrix";

export type RGB = `rgb(${number}, ${number}, ${number})`; // e.g. rgb(255, 255, 255)
export type RGBA = `rgba(${number}, ${number}, ${number}, ${number})`; // e.g. rgba(255, 255, 255, 0.5)
export type HEX = `#${string}`; // e.g. #ffffff
export type CssColor = RGB | RGBA | HEX | string;
export type CssFont = string; // font shorthand: https://developer.mozilla.org/en-US/docs/Web/CSS/font

/**
 * Interactive element properties for SVG shapes.
 *
 * This type supports two scenarios:
 * 1. Static visual elements - No id or event handlers (e.g., decorative shapes)
 * 2. Interactive elements - Required id + at least one event handler (onClick, onPointerDown, onPointerEnter, or onPointerOut)
 *
 * The type system enforces:
 * - You cannot provide event handlers without an id (handlers need the id to identify which element was interacted with)
 * - You cannot provide an id without at least one handler (an id without interactivity serves no purpose)
 */
type Interactive<T> =
    | (T & {
          id?: string;
          onClick?: never;
          onPointerDown?: never;
          onPointerEnter?: never;
          onPointerOut?: never;
      })
    | (T & {
          id: string;
      } & (
              | {
                    onClick: (id: string, event: PointerEvent) => void;
                    onPointerDown?: (id: string, event: PointerEvent) => void;
                    onPointerEnter?: (id: string, event: PointerEvent) => void;
                    onPointerOut?: (id: string, event: PointerEvent) => void;
                }
              | {
                    onClick?: (id: string, event: PointerEvent) => void;
                    onPointerDown: (id: string, event: PointerEvent) => void;
                    onPointerEnter?: (id: string, event: PointerEvent) => void;
                    onPointerOut?: (id: string, event: PointerEvent) => void;
                }
              | {
                    onClick?: (id: string, event: PointerEvent) => void;
                    onPointerDown?: (id: string, event: PointerEvent) => void;
                    onPointerEnter: (id: string, event: PointerEvent) => void;
                    onPointerOut?: (id: string, event: PointerEvent) => void;
                }
              | {
                    onClick?: (id: string, event: PointerEvent) => void;
                    onPointerDown?: (id: string, event: PointerEvent) => void;
                    onPointerEnter?: (id: string, event: PointerEvent) => void;
                    onPointerOut: (id: string, event: PointerEvent) => void;
                }
          ));

export type Anchorable = {
    anchor: vec3;
    anchorOffset?: vec2; // The position of the svg, relative to the origin of the viewBox, on which the svg is to be anchored. The svg renderer anchors at the anchor point and then applies offset. So, the anchorOffset should be defined relative to the viewBox.
};
export type AnchorableLine = {
    anchorStart: vec3;
    anchorEnd: vec3;
};

type RadiusEllipseBase = {
    center: vec2;
    radius: vec2;
    stroke: CssColor;
    fill?: CssColor;
    opacity?: number;
    strokeWidth: number;
    zIndex?: number;
    cursor?: string;
};
export type RadiusEllipse = Interactive<RadiusEllipseBase>;

type BoxEllipseBase = {
    min: vec2;
    max: vec2;
    stroke: CssColor;
    fill?: CssColor;
    opacity?: number;
    strokeWidth: number;
    zIndex?: number;
    cursor?: string;
};
export type BoxEllipse = Interactive<BoxEllipseBase>;

type LineBase = {
    start: vec2;
    end: vec2;
    stroke: CssColor;
    strokeWidth: number;
    strokeDasharray?: string;
    /**
     * Defaults to the SVG `butt` cap. Use `round` when consecutive lines form one
     * visual stroke (e.g. freehand), so the joins between segments are filled in.
     */
    strokeLinecap?: "butt" | "round" | "square";
    opacity?: number;
    zIndex?: number;
};
export type Line = Interactive<LineBase>;

type ArrowBase = {
    start: vec2;
    end: vec2;
    stroke: CssColor;
    strokeWidth: number;
    zIndex?: number;
};
export type Arrow = Interactive<ArrowBase>;

type TextBase = {
    origin: vec2;
    text: string;
    font?: CssFont;
    fill: CssColor;
    zIndex?: number;
};
export type Text = Interactive<TextBase>;

type RectBase = {
    width: number;
    height: number;
    x: number;
    y: number;
    radius?: vec2;
    opacity?: number;
    fill?: CssColor;
    stroke?: CssColor;
    strokeWidth?: number;
    zIndex?: number;
    cursor?: string;
};
export type Rect = Interactive<RectBase>;

type SvgContentBase = {
    origin: vec2;
    content: string;
    scale?: number;
    opacity?: number;
    zIndex?: number;
    cursor?: string;
    /**
     * Opt-in to the `<defs>` + `<use>` instancing path.
     * Defaults to `false`, which preserves the legacy `drawSvgContent` behavior
     * (nested `<svg>` per item).
     * This is an advanced performance optimization and should only be set when
     * the SVG template is stable across instances (same shape, varying scale/styles).
     */
    useInstancing?: boolean;
    /**
     * CSS custom properties applied to the `<use>` element.
     * These cascade into the instance shadow DOM and can override `var(--...)`
     * declarations inside the template.
     * Only used when `useInstancing` is `true`.
     * Keys must be valid CSS custom property names (start with `--`).
     */
    cssVars?: Record<string, string>;
};
export type SvgContent = Interactive<SvgContentBase>;

export type SvgData = {
    ellipses?: (BoxEllipse | RadiusEllipse)[];
    lines?: Line[];
    arrows?: Arrow[];
    texts?: Text[];
    rects?: Rect[];
    svgContents?: SvgContent[];
};
export type AnchoredBoxEllipse = BoxEllipse & Anchorable;
export type AnchoredRadiusEllipse = Omit<RadiusEllipse, "center"> & Anchorable;
export type AnchoredLine = Omit<Line, "start" | "end"> & AnchorableLine;
export type AnchoredArrow = Omit<Arrow, "start" | "end"> & AnchorableLine;
export type AnchoredText = Omit<Text, "origin"> & Anchorable;
export type AnchoredRect = Omit<Rect, "x" | "y"> & Anchorable;
export type AnchoredSvgContent = Omit<SvgContent, "origin"> &
    Anchorable & { anchorOffsetCoordinateType?: "screen" | "viewBox" };
export type AnchoredSvgData = {
    ellipses?: (AnchoredBoxEllipse | AnchoredRadiusEllipse)[];
    lines?: AnchoredLine[];
    arrows?: AnchoredArrow[];
    texts?: AnchoredText[];
    rects?: AnchoredRect[];
    svgContents?: AnchoredSvgContent[];
};

type AnyEllipse = BoxEllipse | RadiusEllipse | AnchoredBoxEllipse | AnchoredRadiusEllipse;
export const isRadiusEllipse = (shape: AnyEllipse): shape is RadiusEllipse | AnchoredRadiusEllipse => {
    return (shape as RadiusEllipse).radius !== undefined;
};
export const isBoxEllipse = (shape: AnyEllipse): shape is BoxEllipse | AnchoredBoxEllipse => {
    const s = shape as BoxEllipse;
    return s.min !== undefined && s.max !== undefined;
};

export type ExtendedElements = Array<{
    type: keyof SvgData;
    element: RadiusEllipse | BoxEllipse | Line | Arrow | Text | Rect | SvgContent;
    depth?: number;
}>;
